import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCalendarEvent,
  IconCalendarPlus,
  IconFileText,
  IconFilter,
  IconStack2,
  IconSunHigh,
} from '@tabler/icons-react';
import { useAuth } from '../../../features/auth';
import {
  Assignment,
  createAssignment,
} from '../../../entities/assignment';
import { useBoard } from '../../../entities/board';
import { useLeads } from '../../../entities/lead';
import { Lead, HistoryEntry, ActionHistoryEntry } from '../../../entities/lead/model/types';
import { CardModal, useCardModalStore } from '../../../features/card-modal';
import { Topbar } from '../../../widgets/topbar';
import { MainSidebar, useSidebar } from '../../../widgets/main-sidebar';
import { MultiInput } from '../../../shared/ui/MultiInput';
import { Select } from '../../../shared/ui/Select';
import { UserSearchInput } from '../../../shared/ui/UserSearchInput/UserSearchInput';
import { formatDateTime, getManagerColor, getManagerInitials, toLocalISOString } from '../../../shared/lib/utils';
import { pb } from '../../../shared/api/pocketbase';
import { Notify, useNotify } from '../../../shared/ui/Notify/Notify';
import { CreateTaskModal, CreateForm } from './CreateTaskModal';
import { TasksColumn, AssignmentCardProps, TasksColumnHandle } from './TasksColumn';

const COLUMN_OPTIONS = [
  { value: 'overdue', label: 'Просроченные' },
  { value: 'today', label: 'Сегодня' },
  { value: 'tomorrow', label: 'Завтра' },
];

const TASK_ROLE_OPTIONS = [
  { value: 'all', label: 'Все' },
  { value: 'created', label: 'Создана мной' },
  { value: 'assigned', label: 'Назначена на меня' },
];

const AssignmentCard: React.FC<AssignmentCardProps> = ({ assignment, onCardClick }) => {
  const authorName = assignment.expand?.authorId?.name ?? 'Неизвестно';
  const authorId = assignment.expand?.authorId?.id ?? authorName;
  const authorEmail = assignment.expand?.authorId?.email;
  const ownerName = assignment.expand?.ownerId?.name;
  const ownerId = assignment.expand?.ownerId?.id ?? ownerName ?? '';
  const ownerEmail = assignment.expand?.ownerId?.email;
  const leadTitle = assignment.expand?.tasksId?.title;

  const authorInitials = getManagerInitials(authorName, authorEmail);
  const authorColor = getManagerColor(authorId);
  const ownerInitials = ownerName ? getManagerInitials(ownerName, ownerEmail) : null;
  const ownerColor = ownerName ? getManagerColor(ownerId) : null;

  return (
    <div className="asgn-card" onClick={() => onCardClick?.(assignment.tasksId!)}>
      <div className="asgn-card-title">{assignment.name}</div>

      <div className="asgn-card-people">
        <div className="asgn-card-person" title={`Поставил: ${authorName}`}>
          <div className="mgr-av" style={{ background: authorColor, width: 24, height: 24, fontSize: 10 }}>
            {authorInitials}
          </div>
          <span className="asgn-card-person-name">{authorName}</span>
        </div>
        {ownerInitials && ownerColor && ownerName && (
          <>
            <IconArrowRight size={12} stroke={2} className="asgn-card-arrow" />
            <div className="asgn-card-person asgn-card-owner" title={`Исполнитель: ${ownerName}`}>
              <div className="mgr-av" style={{ background: ownerColor, width: 24, height: 24, fontSize: 10 }}>
                {ownerInitials}
              </div>
              <span className="asgn-card-person-name">{ownerName}</span>
            </div>
          </>
        )}
      </div>

      <div className="asgn-card-foot">
        <div className="asgn-card-datetime">
          <IconCalendarEvent size={13} stroke={2} />
          <span>{formatDateTime(assignment.planDate)}</span>
        </div>
        {leadTitle && assignment.tasksId && (
          <div className="asgn-card-task-link">
            <IconFileText size={12} stroke={2} />
            <span>{leadTitle}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const TasksPage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { toggle: toggleSidebar } = useSidebar();
  const { stages } = useBoard();
  const { updateLead, getLeadHistory, getLeadActions, addHistoryEntry } = useLeads();
  const cardModal = useCardModalStore();
  const queryClient = useQueryClient();
  const { msg, notify, clear } = useNotify();
  const activeLeadIdRef = useRef<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [cardNotFound, setCardNotFound] = useState(false);
  const [cardNotFoundDismissed, setCardNotFoundDismissed] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Filter state
  const [filterUserIds, setFilterUserIds] = useState<string[]>(() => currentUser ? [currentUser.id] : []);
  const [filterColumnKeys, setFilterColumnKeys] = useState<string[]>([]);
  const [filterTaskRole, setFilterTaskRole] = useState<string>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [localFilterUserIds, setLocalFilterUserIds] = useState<string[]>(() => currentUser ? [currentUser.id] : []);
  const [localFilterColumnKeys, setLocalFilterColumnKeys] = useState<string[]>([]);
  const filterWrapRef = useRef<HTMLDivElement>(null);

  // Per-column total counts for the header
  const [overdueCount, setOverdueCount] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [tomorrowCount, setTomorrowCount] = useState(0);

  // Refs for routing realtime events to each column
  const overdueColRef = useRef<TasksColumnHandle>(null);
  const todayColRef = useRef<TasksColumnHandle>(null);
  const tomorrowColRef = useRef<TasksColumnHandle>(null);

  const canUseFilters = currentUser?.role === 'ADMIN' || currentUser?.role === 'MANAGER';

  // Load selected lead when modal opens
  useEffect(() => {
    if (!cardModal.selectedLeadId) {
      setSelectedLead(null);
      setCardNotFound(false);
      setCardNotFoundDismissed(false);
      return;
    }
    setCardNotFound(false);
    setCardNotFoundDismissed(false);
    pb.collection('tasks').getOne<Lead>(cardModal.selectedLeadId, {
      expand: 'userId,boardId',
      fields: '*,expand.userId.id,expand.userId.name,expand.userId.email,expand.boardId.id,expand.boardId.name,expand.boardId.colors',
    })
      .then(setSelectedLead)
      .catch((err: { status?: number }) => {
        if (err?.status === 404) {
          setCardNotFound(true);
        } else {
          console.error(err);
        }
      });
  }, [cardModal.selectedLeadId]);

  // History & actions queries
  const activeLeadId = cardModal.isOpen ? cardModal.selectedLeadId : null;

  const { data: modalHistory = [] } = useQuery({
    queryKey: ['lead', activeLeadId, 'history'],
    queryFn: () => getLeadHistory(activeLeadId!),
    enabled: Boolean(activeLeadId),
    staleTime: 2_000,
  });

  const { data: modalActions = [] } = useQuery({
    queryKey: ['lead', activeLeadId, 'actions'],
    queryFn: () => getLeadActions(activeLeadId!),
    enabled: Boolean(activeLeadId),
    staleTime: 2_000,
  });

  // Realtime subscriptions when modal is open
  useEffect(() => {
    if (!cardModal.isOpen || !cardModal.selectedLeadId) return;
    const leadId = cardModal.selectedLeadId;
    activeLeadIdRef.current = leadId;

    pb.collection('comments').subscribe<HistoryEntry>('*', async (e) => {
      if (e.record.taskId !== leadId) return;
      if (e.action === 'delete') {
        queryClient.setQueryData<HistoryEntry[]>(['lead', leadId, 'history'], prev =>
          (prev ?? []).filter(h => h.id !== e.record.id),
        );
        return;
      }
      try {
        const hydrated = await pb.collection('comments').getOne<HistoryEntry>(e.record.id, {
          expand: 'userId',
          fields: 'id,taskId,text,created,expand.userId.id,expand.userId.name',
        });
        if (activeLeadIdRef.current !== leadId) return;
        queryClient.setQueryData<HistoryEntry[]>(['lead', leadId, 'history'], prev => {
          const list = prev ?? [];
          if (e.action === 'create') return list.some(h => h.id === hydrated.id) ? list : [hydrated, ...list];
          return list.map(h => h.id === hydrated.id ? hydrated : h);
        });
      } catch {}
    });

    pb.collection('history').subscribe<ActionHistoryEntry>('*', async (e) => {
      if (e.record.taskId !== leadId) return;
      if (e.action === 'delete') {
        queryClient.setQueryData<ActionHistoryEntry[]>(['lead', leadId, 'actions'], prev =>
          (prev ?? []).filter(a => a.id !== e.record.id),
        );
        return;
      }
      try {
        const hydrated = await pb.collection('history').getOne<ActionHistoryEntry>(e.record.id, {
          expand: 'userId',
          fields: 'id,taskId,action,created,expand.userId.id,expand.userId.name',
        });
        if (activeLeadIdRef.current !== leadId) return;
        queryClient.setQueryData<ActionHistoryEntry[]>(['lead', leadId, 'actions'], prev => {
          const list = prev ?? [];
          if (e.action === 'create') return list.some(a => a.id === hydrated.id) ? list : [hydrated, ...list];
          return list.map(a => a.id === hydrated.id ? hydrated : a);
        });
      } catch {}
    });

    return () => {
      activeLeadIdRef.current = null;
      pb.collection('comments').unsubscribe('*');
      pb.collection('history').unsubscribe('*');
    };
  }, [cardModal.isOpen, cardModal.selectedLeadId, queryClient]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (isFilterOpen && filterWrapRef.current && !filterWrapRef.current.contains(e.target as Node)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isFilterOpen]);

  // Single realtime subscription — route events to column refs
  useEffect(() => {
    pb.collection('jobsTasks').subscribe<Assignment>('*', async (e) => {
      if (e.action === 'delete') {
        const record = e.record as Assignment;
        overdueColRef.current?.handleRealtimeEvent('delete', record);
        todayColRef.current?.handleRealtimeEvent('delete', record);
        tomorrowColRef.current?.handleRealtimeEvent('delete', record);
        return;
      }
      try {
        const hydrated = await pb.collection('jobsTasks').getOne<Assignment>(e.record.id, {
          expand: 'authorId,ownerId,tasksId',
          fields: 'id,name,planDate,status,result,tasksId,authorId,ownerId,expand.authorId.id,expand.authorId.name,expand.authorId.email,expand.ownerId.id,expand.ownerId.name,expand.ownerId.email,expand.tasksId.id,expand.tasksId.title',
        });
        overdueColRef.current?.handleRealtimeEvent(e.action, hydrated);
        todayColRef.current?.handleRealtimeEvent(e.action, hydrated);
        tomorrowColRef.current?.handleRealtimeEvent(e.action, hydrated);
      } catch {}
    });

    return () => {
      pb.collection('jobsTasks').unsubscribe('*');
    };
  }, []);

  const handleSave = async (data: Partial<Lead>) => {
    if (!cardModal.selectedLeadId) return;
    try {
      await updateLead(cardModal.selectedLeadId, data);
      cardModal.closeModal();
      notify('Изменения сохранены');
    } catch {
      notify('Ошибка при сохранении');
    }
  };

  const handleAddComment = async (text: string) => {
    if (!cardModal.selectedLeadId) return;
    try {
      await addHistoryEntry(cardModal.selectedLeadId, text);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lead', cardModal.selectedLeadId, 'history'] }),
        queryClient.invalidateQueries({ queryKey: ['lead', cardModal.selectedLeadId, 'actions'] }),
      ]);
      notify('Комментарий добавлен');
    } catch {
      notify('Ошибка при добавлении комментария');
    }
  };

  const handleReloadCardActivity = async () => {
    if (!cardModal.selectedLeadId) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['lead', cardModal.selectedLeadId, 'history'] }),
      queryClient.invalidateQueries({ queryKey: ['lead', cardModal.selectedLeadId, 'actions'] }),
    ]);
  };

  const handleCloseCreateModal = useCallback(() => {
    setShowCreate(false);
  }, []);

  const handleOpenCreateModal = useCallback(() => {
    setShowCreate(true);
  }, []);

  const handleCreate = async (values: CreateForm) => {
    setIsSaving(true);
    try {
      const planDate = toLocalISOString(values.planDate);
      await createAssignment({
        name: values.name.trim(),
        tasksId: values.tasksId,
        ownerId: values.ownerId,
        planDate,
      });
      handleCloseCreateModal();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentUser) return null;

  const hasActiveFilters = filterUserIds.length > 0 || filterColumnKeys.length > 0 || filterTaskRole !== 'all';
  const totalActive = overdueCount + todayCount + tomorrowCount;

  const handleOpenFilter = () => {
    setLocalFilterUserIds(filterUserIds);
    setLocalFilterColumnKeys(filterColumnKeys);
    setIsFilterOpen(prev => !prev);
  };

  const handleApplyFilter = () => {
    setFilterUserIds(localFilterUserIds);
    setFilterColumnKeys(localFilterColumnKeys);
    setIsFilterOpen(false);
  };

  const handleResetFilter = () => {
    setLocalFilterUserIds([]);
    setLocalFilterColumnKeys([]);
    setFilterUserIds([]);
    setFilterColumnKeys([]);
    setFilterTaskRole('all');
    setIsFilterOpen(false);
  };

  const effectiveFilterUserIds =
    filterTaskRole === 'created' || filterTaskRole === 'assigned'
      ? [currentUser!.id]
      : filterUserIds;

  const effectiveFilterMode: 'any' | 'author' | 'owner' =
    filterTaskRole === 'created' ? 'author' :
    filterTaskRole === 'assigned' ? 'owner' :
    'any';

  const showOverdue = filterColumnKeys.length === 0 || filterColumnKeys.includes('overdue');
  const showToday = filterColumnKeys.length === 0 || filterColumnKeys.includes('today');
  const showTomorrow = filterColumnKeys.length === 0 || filterColumnKeys.includes('tomorrow');

  const renderCard = (props: AssignmentCardProps) => <AssignmentCard {...props} />;

  return (
    <div id="app-screen">
      <Topbar user={currentUser} onLogout={logout} onSidebarToggle={toggleSidebar} />
      <div className="app-body">
        <MainSidebar isAdmin={currentUser.role === 'ADMIN'} isManager={currentUser.role === 'MANAGER'} />
        <div className="app-content">
      <div className="tasks-wrap">
        <div className="tasks-header">
          <div className="tasks-header-left">
            <div className="tasks-header-title">Задачи</div>
            <div className="tasks-header-total">{totalActive} активных</div>
          </div>
          <div className="tasks-header-right">
            {canUseFilters && (
              <div className="filter-wrap" ref={filterWrapRef}>
                <button
                  className={`btn-fi${isFilterOpen ? ' active' : ''}`}
                  onClick={handleOpenFilter}
                  title="Фильтры"
                  type="button"
                >
                  <IconFilter size={16} stroke={2} />
                  <div className={`fdot${hasActiveFilters ? ' on' : ''}`} />
                </button>
                <div className={`fdrop${isFilterOpen ? ' open' : ''}`}>
                  <div className="fdrop-title">Фильтрация</div>
                  <div className="fd-grid">
                    <div className="fd-field">
                      <label>Задача</label>
                      <Select
                        options={TASK_ROLE_OPTIONS}
                        value={filterTaskRole}
                        onChange={setFilterTaskRole}
                      />
                    </div>
                    <div className="fd-field">
                      <label>Менеджер</label>
                      <UserSearchInput
                        mode="multi"
                        value={localFilterUserIds}
                        onChange={setLocalFilterUserIds}
                        placeholder="Все менеджеры"
                        initialOptions={currentUser ? [{ value: currentUser.id, label: currentUser.name }] : []}
                      />
                    </div>
                    <div className="fd-field">
                      <label>Колонка</label>
                      <MultiInput
                        options={COLUMN_OPTIONS}
                        value={localFilterColumnKeys}
                        onChange={setLocalFilterColumnKeys}
                        placeholder="Все колонки"
                        icon={<IconStack2 size={15} stroke={1.9} />}
                      />
                    </div>
                  </div>
                  <div className="fd-btns">
                    <button className="fd-reset" onClick={handleResetFilter} type="button">Сброс</button>
                    <button className="fd-apply" onClick={handleApplyFilter} type="button">Применить</button>
                  </div>
                </div>
              </div>
            )}
            <button className="btn-create-lead" type="button" onClick={handleOpenCreateModal}>
              <IconCalendarPlus size={15} stroke={2.2} />
              Новое задание
            </button>
          </div>
        </div>

        <div className="tasks-cols">
          {showOverdue && (
            <TasksColumn
              ref={overdueColRef}
              colKey="overdue"
              label="Просроченные"
              icon={<IconAlertTriangle size={15} stroke={2} />}
              cls="tasks-col-overdue"
              emptyText="Нет просроченных"
              filterUserIds={effectiveFilterUserIds}
              filterMode={effectiveFilterMode}
              onCountChange={setOverdueCount}
              onCardClick={cardModal.openModal}
              renderCard={renderCard}
            />
          )}
          {showToday && (
            <TasksColumn
              ref={todayColRef}
              colKey="today"
              label="Сегодня"
              icon={<IconSunHigh size={15} stroke={2} />}
              cls="tasks-col-today"
              emptyText="На сегодня заданий нет"
              filterUserIds={effectiveFilterUserIds}
              filterMode={effectiveFilterMode}
              onCountChange={setTodayCount}
              onCardClick={cardModal.openModal}
              renderCard={renderCard}
            />
          )}
          {showTomorrow && (
            <TasksColumn
              ref={tomorrowColRef}
              colKey="tomorrow"
              label="Завтра"
              icon={<IconCalendarPlus size={15} stroke={2} />}
              cls="tasks-col-tomorrow"
              emptyText="На завтра заданий нет"
              filterUserIds={effectiveFilterUserIds}
              filterMode={effectiveFilterMode}
              onCountChange={setTomorrowCount}
              onCardClick={cardModal.openModal}
              renderCard={renderCard}
            />
          )}
        </div>
      </div>
        </div>
      </div>

      <CardModal
        lead={selectedLead}
        stages={stages}
        isAdmin={currentUser.role === 'ADMIN'}
        history={modalHistory}
        actions={modalActions}
        isOpen={cardModal.isOpen}
        notFound={cardNotFound && !cardNotFoundDismissed}
        onClose={cardNotFound && !cardNotFoundDismissed ? () => setCardNotFoundDismissed(true) : cardModal.closeModal}
        onSave={handleSave}
        onAddComment={handleAddComment}
        onActivityChanged={handleReloadCardActivity}
        onNotify={notify}
      />

      <CreateTaskModal
        open={showCreate}
        isSaving={isSaving}
        onClose={handleCloseCreateModal}
        onCreate={handleCreate}
      />

      <Notify message={msg} onDone={clear} />
    </div>
  );
};

export { TasksPage };
